import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
  PieChart, Pie, AreaChart, Area, Legend,
} from "recharts";
import { Card } from "../ui/card";
import { GitBranch, PieChart as PieChartIcon, Activity, MapPin, BarChart3, Award } from "lucide-react";

// EduPulse deep-royal-blue theme, per the brand palette.
const NAVY = "#0a1f44";
const BLUE = "#0284c7";
const EMERALD = "#10b981";
const AMBER = "#f59e0b";
const SKY = "#38bdf8";

const FUNNEL_COLORS = [NAVY, BLUE, SKY, AMBER, EMERALD];
export const SOURCE_PALETTE = [BLUE, NAVY, EMERALD, AMBER, "#6366f1", "#ec4899", "#14b8a6", "#a855f7"];

const nf = (n) => Number(n || 0).toLocaleString("en-IN");

function ChartCard({ icon: Icon, title, subtitle, testid, right, height = 260, empty, children }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-slate-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400 truncate">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {empty ? (
        <div className="flex items-center justify-center text-sm text-slate-400" style={{ height }}>
          No data for the current filters.
        </div>
      ) : (
        children
      )}
    </Card>
  );
}

function TooltipShell({ children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      {children}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 1. Ingestion -> conversion funnel                                          //
// --------------------------------------------------------------------------- //

function FunnelTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipShell>
      <div className="font-semibold text-slate-800">{d.stage}</div>
      <div className="text-slate-500 mt-0.5">{nf(d.count)} · {d.pct}% of instances</div>
    </TooltipShell>
  );
}

export function FunnelBarChart({ data }) {
  const stages = data || [];
  const steps = stages.slice(1).map((s, i) => {
    const prev = stages[i];
    return {
      from: prev.stage,
      to: s.stage,
      pct: prev.count ? Math.round((s.count / prev.count) * 100) : 0,
    };
  });

  return (
    <ChartCard icon={GitBranch} title="Ingestion & Conversion Funnel"
      subtitle="Instances → primary → verified → applied → paid" testid="chart-funnel"
      empty={stages.every((s) => !s.count)} height={300}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={stages} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 4 }} barCategoryGap={14}>
          <CartesianGrid horizontal={false} stroke="#EEF2F7" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="stage" width={104} tickLine={false} axisLine={false}
            tick={{ fontSize: 12, fill: "#475569" }} />
          <Tooltip content={<FunnelTooltip />} cursor={{ fill: "#F8FAFC" }} />
          <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={26} isAnimationActive={false}>
            {stages.map((s, i) => <Cell key={s.stage} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}
            <LabelList dataKey="count" position="right" formatter={nf}
              style={{ fontSize: 12, fontWeight: 700, fill: "#0F172A" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {steps.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
          {steps.map((s) => (
            <span key={s.to} data-testid={`funnel-step-${s.to.toLowerCase().replace(/\s+/g, "-")}`}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                s.pct >= 50 ? "bg-emerald-50 text-emerald-700" : s.pct >= 20 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"
              }`}>
              {s.from} → {s.to} <span className="font-mono">{s.pct}%</span>
            </span>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

// --------------------------------------------------------------------------- //
// 2. Channel / source attribution                                            //
// --------------------------------------------------------------------------- //

function SourceTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipShell>
      <div className="flex items-center gap-1.5 font-semibold text-slate-800">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
        {d.source}
      </div>
      <div className="text-slate-500 mt-0.5">{nf(d.count)} leads · {d.pct}%</div>
    </TooltipShell>
  );
}

export function SourceDonutChart({ data }) {
  const rows = data || [];
  const total = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <ChartCard icon={PieChartIcon} title="Channel & Source Attribution"
      subtitle="Share of ingested leads by publisher" testid="chart-source-donut" empty={rows.length === 0}>
      <div className="relative">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={rows} dataKey="count" nameKey="source" innerRadius={64} outerRadius={92}
              paddingAngle={2} strokeWidth={2} stroke="#fff" isAnimationActive={false}>
              {rows.map((d) => <Cell key={d.source} fill={d.color} />)}
            </Pie>
            <Tooltip content={<SourceTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ paddingBottom: 8 }}>
          <div className="text-2xl font-bold text-slate-900 font-mono">{nf(total)}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Total volume</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5 max-h-28 overflow-y-auto">
        {rows.map((d) => (
          <div key={d.source} className="flex items-center justify-between text-xs" data-testid={`source-row-${d.source}`}>
            <span className="flex items-center gap-1.5 text-slate-600 truncate">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
              <span className="truncate">{d.source}</span>
            </span>
            <span className="font-mono text-slate-500 shrink-0 ml-2">{nf(d.count)} · {d.pct}%</span>
          </div>
        ))}
      </div>
    </ChartCard>
  );
}

// --------------------------------------------------------------------------- //
// 3. Lead ingestion velocity                                                 //
// --------------------------------------------------------------------------- //

function VelocityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <TooltipShell>
      <div className="font-semibold text-slate-800">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5 mt-0.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-slate-500">{p.name}: <span className="font-mono text-slate-800">{nf(p.value)}</span></span>
        </div>
      ))}
    </TooltipShell>
  );
}

export function VelocityAreaChart({ data }) {
  const rows = data || [];
  return (
    <ChartCard icon={Activity} title="Lead Ingestion Velocity"
      subtitle="Daily primary leads vs. paid enrollments" testid="chart-velocity"
      empty={rows.every((r) => !r.leads && !r.paid)}>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={rows} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id="velocityLeads" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={BLUE} stopOpacity={0.35} />
              <stop offset="95%" stopColor={BLUE} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="velocityPaid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={EMERALD} stopOpacity={0.4} />
              <stop offset="95%" stopColor={EMERALD} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}
            interval="preserveStartEnd" minTickGap={28} />
          <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
          <Tooltip content={<VelocityTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
            formatter={(v) => <span className="text-slate-600">{v}</span>}
          />
          <Area type="monotone" dataKey="leads" name="Primary Leads" stroke={BLUE} fill="url(#velocityLeads)"
            strokeWidth={2} activeDot={{ r: 4 }} isAnimationActive={false} />
          <Area type="monotone" dataKey="paid" name="Paid Enrollments" stroke={EMERALD} fill="url(#velocityPaid)"
            strokeWidth={2} activeDot={{ r: 4 }} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// --------------------------------------------------------------------------- //
// 4. Geographic distribution                                                 //
// --------------------------------------------------------------------------- //

function StateTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipShell>
      <div className="font-semibold text-slate-800">{d.state}</div>
      <div className="text-slate-500 mt-0.5">{nf(d.leads)} leads · {nf(d.paid)} paid</div>
      <div className="text-slate-500">{d.conversion_pct}% conversion</div>
    </TooltipShell>
  );
}

// --------------------------------------------------------------------------- //
// 5. Real 6-stage pipeline funnel (Lead Captured -> ... -> Enrolled)         //
// --------------------------------------------------------------------------- //

function MilestoneFunnelTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipShell>
      <div className="font-semibold text-slate-800">{d.stage}</div>
      <div className="text-slate-500 mt-0.5">{nf(d.count)} · {d.pct_of_total}% of Lead Captured</div>
      {d.step_conversion_pct != null && (
        <div className="text-slate-500">{d.step_conversion_pct}% carried over from the prior stage</div>
      )}
    </TooltipShell>
  );
}

export function MilestoneFunnelChart({ data }) {
  const stages = data || [];
  return (
    <ChartCard icon={GitBranch} title="Admissions Pipeline Funnel"
      subtitle="Lead Captured → Contacted → Doc Verified → Offer Issued → Fee Paid → Enrolled"
      testid="chart-milestone-funnel" empty={stages.every((s) => !s.count)} height={340}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={stages} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 4 }} barCategoryGap={10}>
          <CartesianGrid horizontal={false} stroke="#EEF2F7" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="stage" width={96} tickLine={false} axisLine={false}
            tick={{ fontSize: 12, fill: "#475569" }} />
          <Tooltip content={<MilestoneFunnelTooltip />} cursor={{ fill: "#F8FAFC" }} />
          <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={26} isAnimationActive={false}>
            {stages.map((s, i) => <Cell key={s.stage} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}
            <LabelList dataKey="count" position="right" formatter={nf}
              style={{ fontSize: 12, fontWeight: 700, fill: "#0F172A" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {stages.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
          {stages.slice(1).map((s) => s.step_conversion_pct == null ? null : (
            <span key={s.stage} data-testid={`milestone-step-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                s.step_conversion_pct >= 50 ? "bg-emerald-50 text-emerald-700"
                  : s.step_conversion_pct >= 20 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"
              }`}>
              → {s.stage} <span className="font-mono">{s.step_conversion_pct}%</span>
            </span>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

// --------------------------------------------------------------------------- //
// 6. Source performance matrix — gross vs. primary, dedup efficiency         //
// --------------------------------------------------------------------------- //

function SourceMatrixTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipShell>
      <div className="font-semibold text-slate-800">{d.source}</div>
      <div className="text-slate-500 mt-0.5">Gross: <span className="font-mono text-slate-800">{nf(d.gross_inquiries)}</span></div>
      <div className="text-slate-500">Primary: <span className="font-mono text-slate-800">{nf(d.primary_leads)}</span></div>
      <div className="text-slate-500">{d.dedup_efficiency_pct}% duplicate noise filtered</div>
    </TooltipShell>
  );
}

export function SourceMatrixChart({ data }) {
  const rows = data || [];
  return (
    <ChartCard icon={BarChart3} title="Source Performance Matrix"
      subtitle="Gross inquiries vs. deduplicated primary leads per channel"
      testid="chart-source-matrix" empty={rows.length === 0} height={Math.max(240, rows.length * 40)}>
      <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 40)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#EEF2F7" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="source" width={96} tickLine={false} axisLine={false}
            tick={{ fontSize: 12, fill: "#475569" }} />
          <Tooltip content={<SourceMatrixTooltip />} cursor={{ fill: "#F8FAFC" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-slate-600">{v}</span>} />
          <Bar dataKey="gross_inquiries" name="Gross Inquiries" fill="#CBD5E1" radius={[0, 4, 4, 0]} maxBarSize={16} isAnimationActive={false} />
          <Bar dataKey="primary_leads" name="Primary Leads" fill={BLUE} radius={[0, 4, 4, 0]} maxBarSize={16} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// --------------------------------------------------------------------------- //
// 7. Counselor SLA leaderboard                                               //
// --------------------------------------------------------------------------- //

// Green >=85 / Yellow 70-84 / Red <70 — shared so the chart bars and the
// leaderboard table badges (Analytics.jsx) always agree on the same cutoffs.
export function slaTone(pct) {
  if (pct == null) return { fill: "#CBD5E1", cls: "bg-slate-100 text-slate-500" };
  if (pct >= 85) return { fill: EMERALD, cls: "bg-emerald-100 text-emerald-800" };
  if (pct >= 70) return { fill: AMBER, cls: "bg-amber-100 text-amber-800" };
  return { fill: "#ef4444", cls: "bg-red-100 text-red-800" };
}

function CounselorSlaTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <TooltipShell>
      <div className="font-semibold text-slate-800">{d.name}</div>
      <div className="text-slate-500 mt-0.5">
        {d.sla_compliance_pct == null ? "No first-contact data yet" : `${d.sla_compliance_pct}% within 2h SLA`}
      </div>
      <div className="text-slate-500">{nf(d.total_assigned_leads)} leads assigned</div>
    </TooltipShell>
  );
}

export function CounselorSlaChart({ data }) {
  const rows = (data || []).filter((r) => r.total_assigned_leads > 0);
  return (
    <ChartCard icon={Award} title="Counselor First-Contact SLA"
      subtitle="% of leads contacted within the 2-hour SLA window"
      testid="chart-counselor-sla" empty={rows.length === 0} height={Math.max(220, rows.length * 34)}>
      <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 34)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 44, left: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#EEF2F7" />
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="name" width={110} tickLine={false} axisLine={false}
            tick={{ fontSize: 12, fill: "#475569" }} />
          <Tooltip content={<CounselorSlaTooltip />} cursor={{ fill: "#F8FAFC" }} />
          <Bar dataKey="sla_compliance_pct" radius={[0, 6, 6, 0]} maxBarSize={18} isAnimationActive={false}>
            {rows.map((r) => <Cell key={r.counselor_id} fill={slaTone(r.sla_compliance_pct).fill} />)}
            <LabelList dataKey="sla_compliance_pct" position="right"
              formatter={(v) => (v == null ? "—" : `${v}%`)}
              style={{ fontSize: 11, fontWeight: 700, fill: "#0F172A" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function StateBarChart({ data }) {
  const rows = data || [];
  return (
    <ChartCard icon={MapPin} title="Geographic Distribution"
      subtitle="Top states by candidate volume" testid="chart-state" empty={rows.length === 0}
      height={Math.max(220, rows.length * 34)}>
      <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 34)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 44, left: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#EEF2F7" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="state" width={92} tickLine={false} axisLine={false}
            tick={{ fontSize: 12, fill: "#475569" }} />
          <Tooltip content={<StateTooltip />} cursor={{ fill: "#F8FAFC" }} />
          <Bar dataKey="leads" fill={BLUE} radius={[0, 6, 6, 0]} maxBarSize={20} isAnimationActive={false}>
            <LabelList dataKey="leads" position="right" formatter={nf}
              style={{ fontSize: 11, fontWeight: 700, fill: "#0F172A" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
